import { useSupportSheet } from "../../components/SupportSheet";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Layout, SupportIcon, AuraLayout } from "lola-framework-ui-test";
import { Toast } from "../../components/Toast";
import {
  GALLERY_NOTICE_FIXTURES,
  GalleryInboxChrome,
  GalleryInboxDrawer,
  GalleryPreferencesSheet,
} from "../../components/notifications/GalleryInbox";
import { TransactionsHistory } from "../../components/TransactionsHistory";
import { SEND_ROUTES } from "../../constants";
import { useSendLiveOperations } from "../../gallery/sendLiveMock";
import { useGalleryCashDepositLaunch } from "../../hooks/useGalleryCashDepositLaunch";
import { useStore } from "../../store/globalState";
import { useComposeStore } from "../../store/useComposeStore";
import type { ITransaction } from "../../interfaces/transactions";
import { BodyCopy } from "../../components/BodyCopy";
import { Navbar } from "../../components/Navbar";

interface HomePageProps {
  originationDisabled?: boolean;
  /** Gallery inspectable: freeze the pending-cash row in face-sweep launch. */
  depositLaunching?: boolean;
  /** Gallery inspectable: open the notifications drawer on first paint. */
  inboxOpen?: boolean;
  /** Gallery inspectable: pin the arrival toast so it can be restyled. */
  toastOpen?: boolean;
  /** Gallery inspectable: open the preferences sheet on first paint. */
  preferencesOpen?: boolean;
}

export const HomePage = memo(({
  originationDisabled = false,
  depositLaunching = false,
  inboxOpen = false,
  toastOpen = false,
  preferencesOpen: preferencesOpenProp = false,
}: HomePageProps) => {
  const { t } = useTranslation("send");
  const { theme, setAmountState } = useStore();
  const { resetCompose } = useComposeStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("send");
  const { launch: launchCashDeposit, payingOperationId } =
    useGalleryCashDepositLaunch({ frozen: depositLaunching });

  const startNewSend = useCallback(() => {
    if (originationDisabled) return;
    resetCompose();
    setAmountState({ amount: "" });
    navigate(SEND_ROUTES.TRANSFER);
  }, [originationDisabled, resetCompose, setAmountState, navigate]);

  const colorConfig = theme.whiteView;
  const { title, backgroundBtn, textColorBtn, dropzoneColors, bodyCopy } = colorConfig;
  const showBell = !originationDisabled;
  const [noticesOpen, setNoticesOpen] = useState(inboxOpen);
  const [preferencesOpen, setPreferencesOpen] = useState(preferencesOpenProp);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [archivedIds, setArchivedIds] = useState<string[]>([]);
  const notices = GALLERY_NOTICE_FIXTURES.filter(
    (item) => !archivedIds.includes(item.id),
  ).map((item) => ({
    id: item.id,
    title: t(item.titleKey),
    body: t(item.bodyKey),
    isRead: item.read || readIds.includes(item.id),
    createdAt: item.createdAt,
  }));

  const liveItems = useSendLiveOperations();

  const items = useMemo<ITransaction[]>(
    () => {
      const mapped = liveItems.map((item) => ({
        ...item,
        launching:
          Boolean(item.cashDepositPending) &&
          (depositLaunching || payingOperationId === item.operationId),
        onDeposit: item.cashDepositPending
          ? () => {
              if (item.operationId) launchCashDeposit(item.operationId);
            }
          : undefined,
        onSelect:
          item.cashDepositPending || !item.operationId
            ? undefined
            : () =>
                navigate(`${SEND_ROUTES.RECEIPT}/${item.operationId}`),
      }));
      if (!depositLaunching) return mapped;
      return [
        ...mapped.filter((item) => item.cashDepositPending),
        ...mapped.filter((item) => !item.cashDepositPending),
      ];
    },
    [liveItems, navigate, launchCashDeposit, payingOperationId, depositLaunching]
  );

  return (
    <>
    <AuraLayout
      colorConfig={colorConfig}
      className={
        showBell
          ? "need-help-header notifications-home-header"
          : "need-help-header"
      }
    >
      <Layout.Header>
        <Navbar
          title={showBell ? undefined : t("common.needHelp")}
          align="right"
          onBackClick={() => navigate(SEND_ROUTES.LANDING)}
          onIconClick={showBell ? undefined : openSupport}
          icon={
            showBell ? (
              <GalleryInboxChrome
                ns="send"
                unread={notices.filter((item) => !item.isRead).length}
                bellColor={title}
                badgeBackground={backgroundBtn}
                badgeTextColor={textColorBtn}
                onOpen={() => setNoticesOpen(true)}
              />
            ) : (
              <SupportIcon size={35} colors={dropzoneColors} />
            )
          }
        />
      </Layout.Header>
      <Layout.Content className="transactions-section">
        <TransactionsHistory items={items} />
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section transactions-footer">
          {originationDisabled && (
            <BodyCopy
              textColor={bodyCopy}
              className="transactions-disabled-note"
            >
              {t("transactions.sendsDisabled")}
            </BodyCopy>
          )}
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            disabled={originationDisabled}
            onClick={startNewSend}
          >
            {t("transactions.newSend")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
      {supportSheet}
      {showBell ? (
        <>
        <GalleryInboxDrawer
          ns="send"
          items={notices}
          open={noticesOpen}
          onClose={() => setNoticesOpen(false)}
          onMarkRead={(id) =>
            setReadIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
          }
          onArchive={(id) =>
            setArchivedIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
          }
          onOpenPreferences={() => {
            setNoticesOpen(false);
            setPreferencesOpen(true);
          }}
        />
        <GalleryPreferencesSheet
          ns="send"
          open={preferencesOpen}
          onClose={() => setPreferencesOpen(false)}
        />
        </>
      ) : null}
      {toastOpen ? (
        <Toast message={t("notifications.exampleToast")} type="info" persist sound />
      ) : null}
    </>
  );
});

export const UserRestrictedHomePage = memo(() => (
  <HomePage originationDisabled />
));

export const InboxPage = memo(() => <HomePage inboxOpen />);

export const PreferencesPage = memo(() => <HomePage preferencesOpen />);

export const ToastPage = memo(() => <HomePage toastOpen />);

export const DepositLaunchingHomePage = memo(() => (
  <HomePage depositLaunching />
));
